import React, { useCallback, useEffect, useState } from 'react';
import { analyserFetchJSON } from '../shared/analyserApi';
import { useDashboardLocale } from '../shared/dashboardLocale';
import { ThreeLineStack } from '../shared/ThreeLineStack';
import { ibkrH3, ibkrMuted } from '../kiwoom/kiwoomTheme';
import {
  getGlobexSessionState,
  scheduleRebalanceRefreshAt,
  sessionStatusColor,
  sessionStatusLabel,
  type GlobexSessionState,
} from './ibkrGlobexSession';

export type IbkrSwingLeg = {
  symbol: string;
  label: string;
  bar_date: string;
  close: number;
  target_action: string;
  target_contracts: number;
  current_qty: number;
  stop_price?: number | null;
  entry_price?: number | null;
  unrealized_usd?: number | null;
  is_open: boolean;
  replay_trades?: number;
  last_exit_date?: string | null;
  last_trade_pnl_usd?: number | null;
};

export type IbkrSwingTrade = {
  symbol: string;
  label: string;
  entry_date?: string | null;
  exit_date?: string | null;
  entry_px: number;
  exit_px: number;
  pnl_usd: number;
  reason: string;
  source?: string;
};

export type IbkrSwingEvent = {
  time_kst: string;
  time_hm: string;
  type: string;
  symbol: string;
  detail: string;
};

export type IbkrSwingLiveResponse = {
  fetched_at?: string;
  mode?: string;
  portfolio?: string;
  equity?: number | null;
  equity_currency?: string | null;
  open_count?: number;
  realized_usd?: number;
  wins?: number;
  next_run_kst?: string | null;
  next_run_et?: string | null;
  timer_active?: boolean | null;
  legs?: IbkrSwingLeg[];
  trades?: IbkrSwingTrade[];
  events?: IbkrSwingEvent[];
  issues?: string[];
};

const th: React.CSSProperties = {
  textAlign: 'left',
  color: '#d81222',
  fontSize: '0.7rem',
  padding: '6px 8px',
  borderBottom: '1px solid #d8122244',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  color: '#f5f0e6',
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: '1px solid #d8122222',
  verticalAlign: 'top',
};

function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div className="ibkr-stat-item">
      <div className="ibkr-stat-label">{label}</div>
      <div className="ibkr-stat-value" style={{ color: color ?? '#f5f0e6' }}>{value}</div>
    </div>
  );
}

function StatusLine({ time, label, color }: { time?: string; label: string; color: string }) {
  return (
    <div className="binance-status-line">
      <span className="binance-status-time">{time ?? '–'}</span>
      <span style={{ color, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function formatTargetLabel(leg: IbkrSwingLeg): string {
  if (leg.target_action === 'LONG' && leg.target_contracts > 0) {
    return `LONG x${leg.target_contracts}`;
  }
  return 'FLAT';
}

function renderLegStatus(leg: IbkrSwingLeg): React.ReactNode {
  const barHm = leg.bar_date?.slice(5) || leg.bar_date || '–';
  const targetColor = leg.target_action === 'LONG' ? '#3fb950' : ibkrMuted;
  const row3Label = leg.is_open ? 'OPEN' : '–';
  const row3Color = leg.is_open ? '#f0b90b' : ibkrMuted;
  const row3Time = leg.is_open && leg.bar_date ? barHm : undefined;

  return (
    <div className="binance-status-stack">
      <StatusLine time={barHm} label="SIGNAL" color={ibkrMuted} />
      <StatusLine time={barHm} label={formatTargetLabel(leg)} color={targetColor} />
      <StatusLine time={row3Time} label={row3Label} color={row3Color} />
    </div>
  );
}

function renderLegPnl(leg: IbkrSwingLeg): React.ReactNode {
  if (leg.is_open && leg.unrealized_usd != null) {
    const v = leg.unrealized_usd;
    return (
      <ThreeLineStack
        mutedColor={ibkrMuted}
        lines={[
          `${v >= 0 ? '+' : ''}${v.toFixed(2)} USD`,
          '(unrealized)',
          null,
        ]}
      />
    );
  }
  if (leg.last_trade_pnl_usd != null) {
    const v = leg.last_trade_pnl_usd;
    return (
      <div style={{ color: v >= 0 ? '#3fb950' : '#f85149' }}>
        <ThreeLineStack
          mutedColor={ibkrMuted}
          lines={[
            `${v >= 0 ? '+' : ''}${v.toFixed(2)} USD`,
            '(last)',
            null,
          ]}
        />
      </div>
    );
  }
  return <ThreeLineStack mutedColor={ibkrMuted} lines={['–', null, null]} />;
}

function renderLegDetail(leg: IbkrSwingLeg): React.ReactNode {
  return (
    <ThreeLineStack
      mutedColor={ibkrMuted}
      lines={[
        `close=${leg.close}`,
        leg.stop_price != null ? `stop=${leg.stop_price}` : null,
        leg.current_qty > 0 ? `qty=${leg.current_qty}` : null,
      ]}
    />
  );
}

function renderTradePnl(pnl: number): React.ReactNode {
  return (
    <div style={{ color: pnl >= 0 ? '#3fb950' : '#f85149' }}>
      <ThreeLineStack
        mutedColor={ibkrMuted}
        lines={[
          `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} USD`,
          null,
          null,
        ]}
      />
    </div>
  );
}

const IbkrDashboardPanel: React.FC = () => {
  const { locale, t } = useDashboardLocale();
  const [data, setData] = useState<IbkrSwingLiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<GlobexSessionState>(() => getGlobexSessionState());

  const load = useCallback(async () => {
    try {
      const next = await analyserFetchJSON<IbkrSwingLiveResponse>(
        'ibkr',
        '/ibkr-swing-live?portfolio=multi',
        { timeoutMs: 45000 },
      );
      setData(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const tick = () => setSession(getGlobexSessionState());
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const refreshAt = scheduleRebalanceRefreshAt(data?.next_run_kst);
    if (!refreshAt) return undefined;
    const delay = refreshAt.getTime() - Date.now();
    if (delay <= 0 || delay > 8 * 24 * 60 * 60 * 1000) return undefined;
    const id = window.setTimeout(() => {
      setLoading(true);
      load();
    }, delay);
    return () => window.clearTimeout(id);
  }, [data?.next_run_kst, load]);

  const legs = data?.legs ?? [];
  const trades = data?.trades ?? [];

  return (
    <div
      className="ibkr-swing-panel"
      style={{
        padding: '14px 16px',
        borderRadius: 8,
        border: '1px solid #d8122266',
        background: 'linear-gradient(135deg, rgba(216,18,34,0.08) 0%, rgba(0,0,0,0.35) 100%)',
      }}
    >
      <div className="binance-alert-header">
        <div className="binance-alert-header-copy">
          <h3 style={{ ...ibkrH3, marginTop: 0, marginBottom: 4 }}>Swing Paper</h3>
          <div className="ibkr-alert-subtitle" style={{ color: ibkrMuted, fontSize: '0.72rem', lineHeight: 1.45 }}>
            {t('ibkr.swing.subtitle.line1')}
            <br />
            {t('ibkr.swing.subtitle.line2')}
            <br />
            {t('ibkr.swing.subtitle.line3')}
          </div>
        </div>
        <button
          type="button"
          className="ibkr-alert-refresh"
          onClick={() => { setLoading(true); load(); }}
        >
          {t('common.refresh')}
        </button>
      </div>

      {loading && !data && <div style={{ color: ibkrMuted, fontSize: '0.78rem' }}>{t('common.loading')}</div>}
      {error && (
        <div style={{ color: '#f85149', fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>
      )}

      {data && (
        <>
          <div className="ibkr-alert-stats">
            <Stat
              label="globex"
              value={sessionStatusLabel(session)}
              color={sessionStatusColor(session)}
            />
            <Stat
              label="equity"
              value={data.equity != null
                ? `${data.equity.toFixed(0)} ${data.equity_currency ?? ''}`.trim()
                : '–'}
            />
            <Stat label="portfolio" value={data.portfolio ?? 'multi'} />
            <Stat label="open" value={data.open_count ?? 0} color="#f0b90b" />
            <Stat
              label="realized"
              value={`${(data.realized_usd ?? 0) >= 0 ? '+' : ''}${(data.realized_usd ?? 0).toFixed(0)} USD`}
              color={(data.realized_usd ?? 0) >= 0 ? '#3fb950' : '#f85149'}
            />
            <Stat label="wins" value={data.wins ?? 0} />
            <Stat
              label="next run"
              value={data.next_run_kst ? data.next_run_kst.slice(5, 16) : '–'}
            />
            <Stat label="mode" value={data.mode ?? 'paper'} />
            <Stat
              label="timer"
              value={data.timer_active == null ? '–' : data.timer_active ? 'on' : 'off'}
              color={data.timer_active ? '#3fb950' : ibkrMuted}
            />
          </div>

          <div className="ibkr-swing-section-title">{t('ibkr.swing.section.legs')}</div>
          <div className="binance-alert-table-wrap">
            <table key={`legs-${locale}`} className="binance-alert-table ibkr-swing-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th className="ibkr-swing-col-symbol" style={th}>{t('common.table.symbol')}</th>
                  <th className="ibkr-swing-col-status" style={th}>{t('common.table.status')}</th>
                  <th className="ibkr-swing-col-pnl" style={th}>{t('common.table.pnl')}</th>
                  <th className="ibkr-swing-col-detail" style={th}>{t('common.table.detail')}</th>
                </tr>
              </thead>
              <tbody>
                {legs.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ ...td, color: ibkrMuted, textAlign: 'center' }}>
                      {t('ibkr.swing.legs.empty')}
                    </td>
                  </tr>
                )}
                {legs.map((leg) => (
                  <tr key={leg.symbol} className={leg.is_open ? 'broker-row-open' : undefined}>
                    <td className="ibkr-swing-col-symbol" style={{ ...td, wordBreak: 'break-word' }}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[leg.label, leg.symbol, leg.bar_date || null]}
                      />
                    </td>
                    <td className="ibkr-swing-col-status" style={td}>{renderLegStatus(leg)}</td>
                    <td className="ibkr-swing-col-pnl" style={td}>{renderLegPnl(leg)}</td>
                    <td className="ibkr-swing-col-detail" style={td}>{renderLegDetail(leg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ibkr-swing-section-title" style={{ marginTop: 14 }}>{t('common.tradeHistory')}</div>
          <div className="binance-alert-table-wrap">
            <table key={`trades-${locale}`} className="binance-alert-table ibkr-swing-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th className="ibkr-swing-col-symbol" style={th}>{t('common.table.symbol')}</th>
                  <th className="ibkr-swing-col-entry" style={th}>{t('common.table.entry')}</th>
                  <th className="ibkr-swing-col-exit" style={th}>{t('common.table.exit')}</th>
                  <th className="ibkr-swing-col-pnl" style={th}>{t('common.table.pnl')}</th>
                  <th className="ibkr-swing-col-reason" style={th}>{t('common.table.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {trades.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ ...td, color: ibkrMuted, textAlign: 'center' }}>
                      {t('ibkr.swing.trades.empty')}
                    </td>
                  </tr>
                )}
                {trades.map((trade, i) => (
                  <tr key={`${trade.symbol}-${trade.exit_date}-${i}`}>
                    <td className="ibkr-swing-col-symbol" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[trade.label, trade.symbol, trade.source || null]}
                      />
                    </td>
                    <td className="ibkr-swing-col-entry" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[trade.entry_date ?? '–', trade.entry_px, null]}
                      />
                    </td>
                    <td className="ibkr-swing-col-exit" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[trade.exit_date ?? '–', trade.exit_px, null]}
                      />
                    </td>
                    <td className="ibkr-swing-col-pnl" style={td}>{renderTradePnl(trade.pnl_usd)}</td>
                    <td className="ibkr-swing-col-reason" style={td}>
                      <ThreeLineStack mutedColor={ibkrMuted} lines={[trade.reason || '–', null, null]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(data.issues?.length ?? 0) > 0 && (
            <div
              style={{
                marginTop: 10,
                padding: '8px 10px',
                borderRadius: 6,
                background: 'rgba(248,81,73,0.08)',
                border: '1px solid #f8514944',
                fontSize: '0.68rem',
                color: '#ffa198',
                lineHeight: 1.4,
              }}
            >
              {data.issues!.map((x, i) => <div key={i}>{x}</div>)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IbkrDashboardPanel;
